/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { ConfirmDialogComponent, NotificationService } from '@alfresco/adf-core';
import { ContentApiService, PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';
import { AssistantService } from '../assistant/assistant.service';
import { FormDraftDialogComponent } from './form-draft-dialog.component';
import { DRAFT_STATE, FormDraftHandover } from './forms.draft';
import { allFields, definitionProblems, FormDefinition, FormStatus, takesValue, withStatus } from './forms.model';
import { FormsService } from './forms.service';

/**
 * Forms: everyone sees the published forms to fill in; administrators also
 * get the drafts and archived ones, with what they can do to each.
 */
@Component({
  selector: 'app-forms-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './forms-page.component.html',
  styleUrls: ['./forms-page.component.scss']
})
export class FormsPageComponent implements OnInit {
  private readonly forms = inject(FormsService);
  private readonly content = inject(ContentApiService);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly assistant = inject(AssistantService);

  readonly all = signal<FormDefinition[]>([]);
  readonly loading = signal(true);
  readonly failed = signal('');
  readonly isAdmin = signal(false);
  /** False when the jobs service cannot print PDFs; filling in would fail at the end. */
  readonly engineUp = signal(true);
  readonly busy = signal<string | null>(null);
  readonly query = signal('');
  /** Filed submissions per form id, for administrators. */
  readonly counts = signal<Record<string, number>>({});

  readonly published = computed(() => this.matching(this.all().filter((f) => f.status === 'published')));
  readonly managed = computed(() => this.matching(this.all()));
  /** Drafting a form from an existing one: administrators, while the assistant's switch for it is on. */
  readonly canDraft = computed(() => this.isAdmin() && !!this.assistant.status()?.drafts);

  ngOnInit(): void {
    this.content
      .getPerson('-me-')
      .pipe(catchError(() => of(null)))
      .subscribe((person: any) => {
        this.isAdmin.set(!!person?.entry?.capabilities?.isAdmin);
        if (this.isAdmin()) {
          this.forms.submissionCounts().subscribe((counts) => this.counts.set(counts));
        }
      });
    this.forms.health().subscribe((up) => this.engineUp.set(up));
    this.assistant.refreshStatus();
    this.reload();
  }

  /** The draft opens in the builder unsaved, for the administrator to check every field before saving. */
  draftWithAi(): void {
    this.dialog
      .open<FormDraftDialogComponent, void, FormDraftHandover>(FormDraftDialogComponent, { width: '560px', maxWidth: '96vw', restoreFocus: true })
      .afterClosed()
      .subscribe((handover) => {
        if (handover) {
          void this.router.navigate(['/forms/new'], { state: { [DRAFT_STATE]: handover } });
        }
      });
  }

  reload(): void {
    this.loading.set(true);
    this.failed.set('');
    this.forms.list().subscribe({
      next: (forms) => {
        this.all.set(forms);
        this.loading.set(false);
      },
      error: (error) => {
        this.failed.set(error?.message ?? String(error));
        this.loading.set(false);
      }
    });
  }

  fieldCount(form: FormDefinition): number {
    return allFields(form).filter(takesValue).length;
  }

  /** Publishing makes the form fillable; archiving takes it off the list without losing it. */
  setStatus(form: FormDefinition, status: FormStatus): void {
    if (status === 'published' && definitionProblems(form).length) {
      this.notification.showError(this.translate.instant('DMS.FORMS.CANNOT_PUBLISH'));
      return;
    }
    this.save(withStatus(form, status), status === 'published' ? 'DMS.FORMS.PUBLISHED' : 'DMS.FORMS.ARCHIVED');
  }

  duplicate(form: FormDefinition): void {
    // a new file, never published: no id and no publishing date of its own
    const copy: FormDefinition = { ...form, title: this.translate.instant('DMS.FORMS.COPY_OF', { title: form.title }), status: 'draft', version: 1 };
    delete copy.id;
    delete copy.publishedAt;
    this.save(copy, 'DMS.FORMS.DUPLICATED');
  }

  remove(form: FormDefinition): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'DMS.FORMS.DELETE_TITLE',
          message: this.translate.instant('DMS.FORMS.DELETE_CONFIRM', { title: form.title }),
          yesLabel: 'DMS.FORMS.DELETE',
          noLabel: 'DMS.UI.7D3094'
        },
        minWidth: '320px',
        restoreFocus: true
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed !== true || !form.id) {
          return;
        }
        this.busy.set(form.id);
        this.forms.remove(form.id).subscribe({
          next: () => {
            this.busy.set(null);
            this.all.update((forms) => forms.filter((f) => f.id !== form.id));
            this.notification.showInfo(this.translate.instant('DMS.FORMS.DELETED', { title: form.title }));
          },
          error: () => {
            this.busy.set(null);
            this.notification.showError(this.translate.instant('DMS.FORMS.SAVE_FAILED'));
          }
        });
      });
  }

  private save(form: FormDefinition, doneKey: string): void {
    this.busy.set(form.id ?? 'new');
    this.forms.save(form).subscribe({
      next: (saved) => {
        this.busy.set(null);
        this.all.update((forms) => {
          const rest = forms.filter((f) => f.id !== saved.id);
          return [...rest, saved].sort((a, b) => a.title.localeCompare(b.title, 'th'));
        });
        this.notification.showInfo(this.translate.instant(doneKey, { title: saved.title }));
      },
      error: () => {
        this.busy.set(null);
        this.notification.showError(this.translate.instant('DMS.FORMS.SAVE_FAILED'));
      }
    });
  }

  private matching(forms: FormDefinition[]): FormDefinition[] {
    const words = this.query().trim().toLowerCase();
    return words ? forms.filter((f) => [f.title, f.description, f.code].some((text) => (text ?? '').toLowerCase().includes(words))) : forms;
  }
}
